<template>
  <div ref="loginContainer" class="min-h-dvh flex flex-col items-center justify-center
           bg-linear-to-br from-blue-50 to-indigo-100 px-4 py-8 sm:px-6
           pb-[calc(2rem+env(safe-area-inset-bottom))]
           pt-[calc(2rem+env(safe-area-inset-top))]" @touchstart="record('ts', $event)"
    @touchmove="record('tm', $event)">

    <div class="w-full max-w-[min(100%,448px)] bg-white rounded-2xl shadow-xl p-6 sm:p-8">

      <div class="flex mb-8 border-b border-gray-100">
        <button @click="isLoginMode = true"
          :class="[isLoginMode ? 'text-blue-700 border-b-2 border-blue-700' : 'text-gray-400']"
          class="flex-1 pb-3 font-bold text-lg transition-all">
          {{ t('account.login') }}
        </button>
        <button @click="isLoginMode = false"
          :class="[!isLoginMode ? 'text-blue-700 border-b-2 border-blue-700' : 'text-gray-400']"
          class="flex-1 pb-3 font-bold text-lg transition-all">
          {{ t('account.register') }}
        </button>
      </div>

      <div class="space-y-4">
        <input v-model="username" @focus="startRecording" @keydown="record('kd', $event)"
          :placeholder="usernamePlaceholder"
          class="w-full rounded-lg border border-gray-300 px-4 py-3 text-base focus:ring-2 focus:ring-blue-700 outline-none transition"
          autocapitalize="none" autocorrect="off" spellcheck="false" />

        <div class="relative">
          <input v-model="password" @focus="startRecording" @keydown="record('kd', $event)"
            :type="showPassword ? 'text' : 'password'" :placeholder="passwordPlaceholder"
            class="w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 text-base focus:ring-2 focus:ring-blue-700 outline-none transition" />
          <button type="button" @click="showPassword = !showPassword"
            class="absolute right-0 top-0 h-full px-3 flex items-center text-gray-400">
            <span class="text-xl">{{ showPassword ? '🙈' : '👁️' }}</span>
          </button>
        </div>
      </div>

      <div class="mt-4 mb-6">
        <div class="flex items-center min-h-[44px]">
          <input id="rememberMe" type="checkbox" v-model="rememberMe" class="appearance-none h-6 w-6 rounded border-2 border-gray-400 bg-white 
                   checked:bg-blue-700 checked:border-blue-700 focus:ring-2 focus:ring-blue-700 
                   relative cursor-pointer transition-all shrink-0
                   after:content-[''] after:absolute after:hidden after:checked:block 
                   after:left-[7px] after:top-[2px] after:w-[6px] after:h-[11px] 
                   after:border-white after:border-r-2 after:border-b-2 after:rotate-45" />
          <label for="rememberMe" class="ml-3 text-gray-700 cursor-pointer select-none">
            {{ t('account.rememberMe') }}
          </label>
        </div>

        <div v-if="!isLoginMode" class="flex items-start min-h-[44px]">
          <input id="agreeTerms" type="checkbox" v-model="agreedTerms" class="appearance-none h-6 w-6 rounded border-2 border-gray-400 bg-white 
                   checked:bg-blue-700 checked:border-blue-700 focus:ring-2 focus:ring-blue-700 
                   relative cursor-pointer transition-all shrink-0 mt-0.5
                   after:content-[''] after:absolute after:hidden after:checked:block 
                   after:left-[7px] after:top-[2px] after:w-[6px] after:h-[11px] 
                   after:border-white after:border-r-2 after:border-b-2 after:rotate-45" />
          <label for="agreeTerms" class="ml-3 text-sm text-gray-600 cursor-pointer select-none leading-relaxed">
            {{ t('account.agreeCheckboxLabel') }}

            <router-link to="/account-terms-serivces" target="_blank"
              class="text-blue-700 underline hover:text-blue-800 font-medium mx-0.5">
              {{ t('account.termsService') }}
            </router-link>

            {{ t('account.and') }}

            <router-link to="/account-privacy" target="_blank"
              class="text-blue-700 underline hover:text-blue-800 font-medium mx-0.5">
              {{ t('account.privacyPolicy') }}
            </router-link>
          </label>
        </div>
      </div>

      <SliderCaptcha ref="captchaRef" @verified="onSliderVerified" @failed="onSliderFailed"
        :label="t('account.captchaLabel')" :hint="t('account.captchaHint')" />

      <div class="mt-6">
        <button v-if="isLoginMode" :disabled="!isVerified" @click="handleLoginClick"
          class="w-full py-3.5 rounded-lg font-bold text-white bg-blue-700 active:bg-blue-800 transition-all shadow-md disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none">
          {{ t('account.login') }}
        </button>

        <button v-else :disabled="!isVerified || !agreedTerms" @click="handleRegisterClick"
          class="w-full py-3.5 rounded-lg font-bold text-white bg-blue-700 active:bg-blue-800 transition-all shadow-md disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none">
          {{ t('account.register') }}
        </button>
      </div>

      <div v-if="error" class="mt-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">{{ error }}
      </div>
      <div v-if="message" class="mt-4 p-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg">{{
        message }}</div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useLogin } from './Login'
import { useI18n } from 'vue-i18n'
import SliderCaptcha from './SliderCaptcha.vue'
import CaptchaComponent from './CaptchaComponent.vue'
import { logger } from '@myorg/type-lib';
import { useRoute } from 'vue-router'
import { api } from '../services/api' // axios instance

const route = useRoute()
const isError = ref(false)
const csrfToken = ref<string>('')

onMounted(() => {
  const queryCsrf = route.query.csrf

  isError.value = route.query.error === 'true'
  csrfToken.value = Array.isArray(queryCsrf) ? (queryCsrf[0] || '') : (queryCsrf || '')

  logger.log('%c[DEBUG 1] 網址列解析出的 Token:', 'background: #222; color: #bada55; font-size: 16px', csrfToken.value)

  if (csrfToken.value) {
    const apiInstance = api();
    
    // 1. 寫入前先檢查裡面有什麼
    logger.log('[DEBUG 2] 寫入前的全域 Headers:', JSON.parse(JSON.stringify(apiInstance.defaults.headers.common)))
    
    // 2. 寫入兩個最常見的 CSRF Header 欄位（以防萬一）
    apiInstance.defaults.headers.common['X-XSRF-TOKEN'] = csrfToken.value;
    apiInstance.defaults.headers.common['X-CSRF-Token'] = csrfToken.value; 
    
    // 3. 寫入後再次檢查
    logger.log('[DEBUG 3] 寫入後的全域 Headers:', apiInstance.defaults.headers.common)
  }
})

const captchaRef = ref<InstanceType<typeof SliderCaptcha> | null>(null)
const { t } = useI18n()
const {
  username, password, error, message, rememberMe,
  doLogin, doRegister, initCaptcha, behaviorLog, 
  sliderPath, target_index, final_x
} = useLogin()

// UI States
const isLoginMode = ref(true)
const agreedTerms = ref(false)
const isVerified = ref(false)
const showPassword = ref(false)
const loginContainer = ref<HTMLElement | null>(null)
const isRecording = ref(false)
const startTime = ref(0)

const usernamePlaceholder = computed(() => t('account.username'))
const passwordPlaceholder = computed(() => t('account.password'))


// Reset verification on mode switch
watch(isLoginMode, () => {
  isVerified.value = false
  error.value = ''
  message.value = ''
  captchaRef.value?.reset()
})

// Behavioral Recording
const startRecording = () => { if (!isRecording.value) { isRecording.value = true; startTime.value = Date.now(); } }
const record = (type: string, e: any) => {
  if (!isRecording.value || !loginContainer.value) return
  const rect = loginContainer.value.getBoundingClientRect()
  let cx = e.touches?.[0]?.clientX ?? e.clientX
  let cy = e.touches?.[0]?.clientY ?? e.clientY
  behaviorLog.value.push({
    t: Date.now() - startTime.value, e: type,
    x: cx !== undefined ? Math.round(cx - rect.left) : null,
    y: cy !== undefined ? Math.round(cy - rect.top) : null,
    k: e.key || null
  })
  if (behaviorLog.value.length > 100) behaviorLog.value.shift()
}

// Captcha Handlers
const onSliderVerified = (data: any) => {
  isVerified.value = true
  sliderPath.value = data.path
  target_index.value = data.targetIndex
  final_x.value = data.finalRatio
}
const onSliderFailed = () => { isVerified.value = false; error.value = "驗證失敗，請重試"; }

// Action Handlers
const handleLoginClick = async () => {
  if (!isVerified.value) return
  try { 
    await doLogin() 
  } catch (err) {  
    logger.log("handleLoginClick catch",err)
    isVerified.value = false; 
    captchaRef.value?.reset();
  } finally { }
}
const handleRegisterClick = async () => {
  if (!isVerified.value || !agreedTerms.value) return
  try { await doRegister() 

  } catch (err) { 
    isVerified.value = false; 
    captchaRef.value?.reset();
  } finally { }
}
</script>